// eslint-disable-next-line no-restricted-imports
import { Trans } from '@lingui/macro';
import { useWeb3React } from '@web3-react/core';
import { getConnection } from 'connection/utils';
import { Warning } from 'phosphor-react';
import { darken } from 'polished';
import { useToggleWalletModal } from 'state/application/hooks';
import { useAppSelector } from 'state/hooks';
import styled, { css } from 'styled-components/macro';
import { shortenAddress } from 'utils';

import { ButtonSecondary } from '../Button';
import StatusIcon from '../Identicon/StatusIcon';
import WalletModal from '../WalletModal';

const Web3StatusGeneric = styled(ButtonSecondary)`
  ${({ theme }) => theme.flexRowNoWrap}
  width: 100%;
  align-items: center;
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  margin-right: 2px;
  margin-left: 2px;
  :focus {
    outline: none;
  }
`;
const Web3StatusError = styled(Web3StatusGeneric)`
  border: 1px solid ${({ theme }) => theme.red1};
  color: ${({ theme }) => theme.white};
  font-weight: 500;
  :hover,
  :focus {
    background-color: ${({ theme }) => darken(0.1, theme.red1)};
  }
`;

const Web3StatusConnect = styled(Web3StatusGeneric)<{ faded?: boolean }>`
  border: none;
  color: ${({ theme }) => theme.text1};
  font-weight: 500;

  :hover,
  :focus {
    border: 2px solid ${({ theme }) => darken(0.05, theme.text1)};
    color: ${({ theme }) => theme.text1};
  }

  ${({ faded }) =>
    faded &&
    css`
      border: 2px solid ${({ theme }) => theme.text1};
      color: ${({ theme }) => theme.text1};

      :hover,
      :focus {
        border: 2px solid ${({ theme }) => darken(0.2, theme.text1)};
        color: ${({ theme }) => darken(0.05, theme.text1)};
      }
    `}
`;

const Web3StatusConnected = styled(Web3StatusGeneric)<{ pending?: boolean }>`
  padding-top: 0.4rem;
  border: 1px solid ${({ pending, theme }) => (pending ? theme.primary1 : theme.text1)};
  color: ${({ pending, theme }) => (pending ? theme.white : theme.text1)};
  font-weight: 500;
  :hover,
  :focus {
    border: 1px solid ${({ theme }) => darken(0.2, theme.text1)};

    :focus {
      border: 1px solid ${({ pending, theme }) => (pending ? darken(0.1, theme.primary1) : darken(0.2, theme.text1))};
    }
  }
`;

const Text = styled.p`
  font-family: 'Presicav';
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0 0.5rem 0 0.25rem;
  font-size: 0.8rem;
  width: fit-content;
  font-weight: 500;
`;

const NetworkIcon = styled(Warning)`
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
`;

function Web3StatusInner() {
  const { account, connector, chainId, ENSName } = useWeb3React();
  const connectionType = getConnection(connector).type;
  const toggleWalletModal = useToggleWalletModal();

  const error = useAppSelector((state) => state.connection.errorByConnectionType[getConnection(connector).type]);

  if (!chainId) {
    return null;
  } else if (error) {
    return (
      <Web3StatusError onClick={toggleWalletModal}>
        <NetworkIcon />
        <Text>
          <Trans>Error</Trans>
        </Text>
      </Web3StatusError>
    );
  } else if (account) {
    return (
      <Web3StatusConnected data-testid="web3-status-connected" onClick={toggleWalletModal}>
        <Text>{ENSName || shortenAddress(account)}</Text>
        <StatusIcon connectionType={connectionType} />
      </Web3StatusConnected>
    );
  } else {
    return (
      <Web3StatusConnect onClick={toggleWalletModal} faded={!account}>
        <Text>
          <Trans>
            CONNECT
            <br />
            WALLET
          </Trans>
        </Text>
      </Web3StatusConnect>
    );
  }
}

export default function Web3Status() {
  const { ENSName } = useWeb3React();

  return (
    <span>
      <Web3StatusInner />
      <WalletModal ENSName={ENSName ?? undefined} />
    </span>
  );
}
